import React, { useState, useEffect, useRef, useCallback } from "react";
import DesktopNavigation from "./DesktopNavigation";
import MobileMenuOverlay from "./MobileMenuOverlay";
import HorizontalTickerHomePage from "@/core/Animation/HorizontalTicker/HorizontalTickerHomePage";
import CartIcon from "@/core/components/Cart/CartIcon";
import Logo from "./Logo";
import { useUI } from "@/core/components/context/UIContext";
import { TickerProvider } from "@/core/Animation/HorizontalTicker/TickerContext";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const { isMobileMenuOpen, toggleMobileMenu } = useUI();

  // Simplified scroll handler - only track scrolled state
  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrolled = currentScrollY > 50; // Higher threshold for better UX

        if (scrolled !== isScrolled) {
          setIsScrolled(scrolled);
        }

        lastScrollY.current = currentScrollY;
        ticking.current = false;
      });
      ticking.current = true;
    }
  }, [isScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        toggleMobileMenu();
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen, toggleMobileMenu]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  return (
    <TickerProvider>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-header transition-all duration-300 ${
          isScrolled ? "bg-gray-900 shadow-lg" : "bg-gray-900 shadow-md"
        }`}
        style={{ height: "var(--header-total-height)" }}
      >
        {/* Ticker */}
        <div
          className="w-full overflow-hidden bg-cannabis-green-500"
          style={{ height: "var(--ticker-height)" }}
        >
          <HorizontalTickerHomePage />
        </div>

        {/* Navigation */}
        <div
          className="container mx-auto px-4 flex justify-between items-center"
          style={{ height: "var(--nav-height)" }}
        >
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <DesktopNavigation />
          </div>

          {/* User and Cart icons */}
          <div className="flex items-center space-x-2">
            <CartIcon />

            {/* Mobile Menu Toggle - Only show when menu is closed */}
            <button
              className={`md:hidden text-white p-2 focus:outline-none ml-1 relative z-[9999] transition-all duration-200 ${
                isMobileMenuOpen
                  ? "bg-cannabis-green-500/20 rounded-lg hidden"
                  : ""
              }`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span
                  className={`block h-0.5 w-6 bg-white transform transition-all duration-200 ease-in-out ${
                    isMobileMenuOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                ></span>
                <span
                  className={`block h-0.5 w-6 bg-white transition-all duration-200 ease-in-out ${
                    isMobileMenuOpen ? "opacity-0" : ""
                  }`}
                ></span>
                <span
                  className={`block h-0.5 w-6 bg-white transform transition-all duration-200 ease-in-out ${
                    isMobileMenuOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <MobileMenuOverlay
          isOpen={isMobileMenuOpen}
          onClose={toggleMobileMenu}
        />
      </header>
    </TickerProvider>
  );
};

export default Header;
